import { QuickAddComponent } from '@theme/quick-add';

/**
 * A floating add-to-cart button for the product page.
 *
 * Extends the theme's own QuickAddComponent so clicking it opens the exact
 * same quick-add drawer used by product-card quick-add buttons on collection
 * pages. The only differences:
 *   - productPageUrl resolves to the current page (there is no product-card
 *     ancestor on the product template).
 *   - The button shows only while the main buy buttons are still below the
 *     viewport (not yet reached). After scrolling past them, the theme's
 *     native sticky-add-to-cart bar takes over unchanged.
 */
class FloatingQuickAddComponent extends QuickAddComponent {
  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  get productPageUrl() {
    // The theme keeps ?variant= in sync on the product page, so the current
    // URL always reflects the shopper's selected variant.
    return window.location.href;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setupVisibilityObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
  }

  #setupVisibilityObserver() {
    const buyButtonsBlock = document.querySelector('.buy-buttons-block');

    if (!buyButtonsBlock) {
      // No buy buttons on the page (unlikely) — just always show the button.
      this.setAttribute('data-visible', 'true');
      return;
    }

    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();
        // Show only while the buy buttons are still BELOW the viewport (the
        // shopper hasn't reached them yet). Once they scroll past them, the
        // theme's native sticky-add-to-cart bar takes over — never both.
        if (rect.top > 0) {
          this.setAttribute('data-visible', 'true');
        } else {
          this.setAttribute('data-visible', 'false');
        }
      } else {
        this.setAttribute('data-visible', 'false');
      }
    });

    this.#buyButtonsObserver.observe(buyButtonsBlock);
  }
}

if (!customElements.get('floating-quick-add-component')) {
  customElements.define('floating-quick-add-component', FloatingQuickAddComponent);
}
